import classNames from 'classnames';
import { AnimatePresence } from 'framer-motion';
import type { ReactNode } from 'react';
import React, { useContext, useState } from 'react';
import { createPortal } from 'react-dom';
import * as styles from './FloatingToolbar.module.scss';
import Slot from '../../../components/Slot';
import ZoomInAnimation from '../../../components/ZoomInAnimation';
import addTestId from '../../../utils/addTestId';
import { ignoreHoveredBlotLeaveClassName } from '../../HoveredBlotListener';

const NEAR_SIDE_HOVER_TOLERANCE = styles.nearSideHoverTolerance;

const ContainerElContext = React.createContext<HTMLElement | null>(null);

interface FloatingToolbarRootProps {
  children: ReactNode;
}

const FloatingToolbarRoot = ({ children }: FloatingToolbarRootProps) => {
  const [containerEl, setContainerEl] = useState<HTMLElement | null>(null);

  return (
    <ContainerElContext.Provider value={containerEl}>
      <Slot ref={setContainerEl}>{children}</Slot>
    </ContainerElContext.Provider>
  );
};

interface FloatingToolbarPortalProps {
  children: ReactNode;
}

const FloatingToolbarPortal = ({ children }: FloatingToolbarPortalProps) => {
  const containerEl = useContext(ContainerElContext);
  return containerEl ? createPortal(children, containerEl) : children;
};

interface FloatingToolbarProps {
  bounds?: { top: number; right: number; left: number } | null;
  children: ReactNode;
  leftAligned?: boolean;
  testId?: string;
}

const FloatingToolbar = ({
  bounds,
  children,
  leftAligned,
  testId,
}: FloatingToolbarProps) => {
  return (
    <FloatingToolbarPortal>
      <AnimatePresence>
        {bounds && (
          <ZoomInAnimation
            data-no-print
            className={classNames(
              styles.hoverWrapper,
              ignoreHoveredBlotLeaveClassName,
              { [styles.leftAligned]: leftAligned },
            )}
            style={{
              top: bounds.top,
              left: leftAligned ? bounds.left : bounds.right,
            }}
            initial={{
              translateY: '-100%',
              translateX: leftAligned
                ? `-${NEAR_SIDE_HOVER_TOLERANCE}`
                : `calc(-100% + ${NEAR_SIDE_HOVER_TOLERANCE})`,
              transformOrigin: leftAligned
                ? `${NEAR_SIDE_HOVER_TOLERANCE} bottom`
                : `calc(100% - ${NEAR_SIDE_HOVER_TOLERANCE}) bottom`,
            }}
          >
            <div
              className={styles.container}
              role="toolbar"
              {...addTestId(testId)}
            >
              {children}
            </div>
          </ZoomInAnimation>
        )}
      </AnimatePresence>
    </FloatingToolbarPortal>
  );
};

export default {
  Root: FloatingToolbarRoot,
  Toolbar: FloatingToolbar,
  Portal: FloatingToolbarPortal,
};
